"use client";
import { DateTime } from "luxon";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { GetCommunity } from "~/app/v1/[communitySlug]/actions";
import { fuzzySearchTagsCommunityPage } from "~/app/_actions/Events";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/shadcn/Select";
import { If } from "~/components/v3/Conditional";
import { MoreInfoTab } from "~/components/v3/community-components/CommunityPageMoreInfo";
import CommunityPagePeopleTab from "~/components/v3/community-components/CommunityPagePeopleTab";
import { EventsGrid } from "~/components/v3/events-components/EventsGrid";
import { EventsNearInput } from "~/components/v3/events-components/EventsNear";
import { Button } from "~/components/v3/themed/Button";
import {
    extractCoordinates,
    GooglePlace,
} from "~/components/v3/themed/GoogleMapsAutoComplete";
import {
    GooeySearchInput,
    InnerSearchIcon,
    SearchInput,
} from "~/components/v3/themed/SearchInput";
import { UserPermissions } from "~/server/api/helpers/permissionHelpers";
import useFilterStore, {
    useSyncSearchWithURL,
} from "~/state/globalFilterStore";
import { useFollowingStore } from "~/state/store";
import { GetAllEventsOutput } from "~/types/EventTypesInferred";
import { api } from "~/utils/api";
import { cn } from "~/utils/fontsConfig";
import ConditionalCommunityBanner from "../banners/ConditionalCommunityBanner";
import CondensedEventList from "../events-components/CondensedEventList/CondensedEventList";
import EventTagsPopoverFilter from "../events-components/EventTagsPopoverFilter";
import MerchTab from "./CommunityMerchTab";
import { Card, CardContent, CardHeader } from "../shadcn/Card";
import { Label } from "../shadcn/Label";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { adjustTzToLocal } from "~/utils/time";
import { debounce } from "~/utils/optimization";
import { TabSwitcherIcons } from "~/app/v1/[communitySlug]/_components/TabSwitcher";

export const CommunityTagsFilter = ({
    community,
    events,
    hideForRevenueCat,
}: {
    community: GetCommunity;
    events: GetAllEventsOutput[] | undefined;
    hideForRevenueCat?: boolean;
}) => {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [open, setOpen] = useState<number>();

    const selectableTags = [
        ...(hideForRevenueCat
            ? []
            : [
                  {
                      name: "Time of Day",
                      tags: ["Morning", "Afternoon", "Evening"],
                  },
              ]),
        ...community.defaultTags,
    ];

    const currentTags = searchParams?.get("ftags")?.split(",") || [];

    const handleSelect = (index: number, value: string, customTags: any) => {
        // Find tags of this group that are already selected
        const tagGroup = currentTags.filter((t) =>
            customTags.tags?.includes(t),
        );
        // filter the old tag from group
        const newTags = currentTags.filter((t) => !tagGroup.includes(t));

        // Add new if it's not a group name
        if (value !== customTags.name) {
            newTags.push(value);
        }

        const params = new URLSearchParams(searchParams ?? "");
        if (newTags.length > 0) {
            params.set("ftags", newTags.join(","));
        } else {
            params.delete("ftags");
        }

        // Using router.replace to not add to history stack
        router.replace(`${pathname}?${params.toString()}`, {
            scroll: false,
        });

        // Setting the event filter on the front-end
        const filteredEvents = events?.filter((e) =>
            newTags.length === 0
                ? true
                : e.tags.some((t) => newTags.includes(t)),
        );
        setOpen(undefined);
    };

    return (
        <div className="flex h-14 w-full items-center gap-4 pl-2 md:max-w-5xl">
            <h5 className="text-lg font-[500] leading-none">Tags</h5>
            <div className="small-scrollbar flex h-full max-w-[calc(100vw-40px-36px)] items-center justify-start gap-2 overflow-x-auto px-1 pb-1">
                {selectableTags.map((customTags, index) => (
                    <div key={index} className="relative">
                        <Select
                            open={open === index}
                            onOpenChange={(isOpen) => {
                                setOpen(isOpen ? index : undefined);
                            }}
                            onValueChange={(value) =>
                                handleSelect(index, value, customTags)
                            }
                        >
                            <SelectTrigger
                                className="h-9 w-full min-w-[150px] max-w-[150px] touch-manipulation rounded-full text-start"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setOpen(open === index ? undefined : index);
                                }}
                            >
                                <SelectValue placeholder={customTags.name} />
                            </SelectTrigger>
                            <SelectContent
                                className="z-50"
                                onTouchEnd={(e) => e.stopPropagation()}
                            >
                                <SelectGroup>
                                    <SelectItem value={customTags.name}>
                                        {customTags.name}
                                    </SelectItem>
                                    {customTags.tags?.map((tagName: string) => (
                                        <SelectItem
                                            key={tagName}
                                            value={tagName}
                                        >
                                            {tagName}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>
                ))}
            </div>
        </div>
    );
};

const calcDayOfWeekNumber = (date: Date) => {
    const day = date.getDay();
    if (day === 0) return 7;
    return day;
};

export const DayOfWeekFilter = ({
    events,
}: {
    events: GetAllEventsOutput[] | undefined;
}) => {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const [daysOfWeek, setDaysOfWeek] = useState<
        Array<{
            month: string;
            weekday: string;
            dayNumber: number;
            startDate: Date;
            weekDayNumber: number;
            hasEvents: boolean;
            isSelected: boolean;
        }>
    >([]);

    const selectedDateString = searchParams?.get("day");

    useEffect(() => {
        if (!events || !events.length) return;
        // Find earliest event date
        const earliestDate = events.reduce(
            (earliest, event) => {
                const eventDate = DateTime.fromJSDate(
                    adjustTzToLocal(event.startTime).toJSDate(),
                );
                return DateTime.min(earliest, eventDate);
            },
            DateTime.now().plus({ years: 10 }) as DateTime, // far in the future
        );

        // Create array of 6 consecutive days starting from earliest
        const daysArray = Array.from({ length: 6 }, (_, i) => {
            const date = earliestDate.plus({ days: i }).startOf("day");
            const jsDate = date.toJSDate();

            // Check if there are events on this day
            const hasEventsOnDay = events.some((event) => {
                const eventDate = DateTime.fromJSDate(
                    adjustTzToLocal(event.startTime).toJSDate(),
                ).startOf("day");
                return eventDate.hasSame(date, "day");
            });

            // Check if this day is selected
            const isSelected =
                selectedDateString === date.toFormat("yyyy-MM-dd");

            return {
                month: date.toFormat("MMM"),
                weekday: date.toFormat("cccc"),
                dayNumber: parseInt(date.toFormat("d")),
                startDate: jsDate,
                weekDayNumber: calcDayOfWeekNumber(jsDate),
                hasEvents: hasEventsOnDay,
                isSelected,
            };
        });

        setDaysOfWeek(daysArray);
    }, [events, selectedDateString]);

    const handleDaySelect = (date: Date) => {
        const current = new URLSearchParams(
            Array.from(searchParams?.entries() ?? []),
        );
        const dayToCompare = selectedDateString
            ? DateTime.fromFormat(selectedDateString, "yyyy-MM-dd")
                  .startOf("day")
                  .toJSDate()
            : undefined;

        const isCurrentlySelected =
            dayToCompare &&
            DateTime.fromJSDate(dayToCompare)
                .startOf("day")
                .hasSame(DateTime.fromJSDate(date).startOf("day"), "day");

        if (isCurrentlySelected) {
            current.delete("day");
        } else {
            const formattedDate =
                DateTime.fromJSDate(date).toFormat("yyyy-MM-dd");
            current.set("day", formattedDate);
        }

        const search = current.toString();
        const query = search ? `?${search}` : "";

        router.replace(`${pathname}${query}`, {
            scroll: false,
        });
    };

    if (!events) return null;

    return (
        <div
            className={cn(
                "flex px-2 md:gap-4 md:px-0",
                daysOfWeek.length < 6 && "gap-2",
                daysOfWeek.length === 6 && "justify-between",
            )}
        >
            {daysOfWeek.map((d, index) => (
                <button
                    key={index}
                    disabled={!d.hasEvents}
                    className={cn(
                        "flex h-[80px] max-w-[145px] flex-col items-center justify-center rounded-lg bg-v3-neutral p-4 text-lg font-[500] md:hover:bg-white md:hover:text-v3-neutral",
                        d.isSelected && "bg-white text-v3-neutral",
                        !d.isSelected &&
                            "md:hover:bg-white md:hover:text-v3-neutral",
                        "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-v3-neutral disabled:hover:text-white",
                    )}
                    style={{
                        width: `calc((100vw/${daysOfWeek.length + 1}))`,
                    }}
                    onClick={() => handleDaySelect(d.startDate)}
                >
                    <span className="flex w-full items-center justify-center">
                        {d.month} {d.dayNumber}
                    </span>
                    <span className="hidden text-sm md:block">{d.weekday}</span>
                    <span className="block text-sm md:hidden">
                        {d.weekday.slice(0, 1)}
                    </span>
                </button>
            ))}
        </div>
    );
};

const CommunityEventsFeed = ({
    community,
    approxLocation,
    permissions,
    refetch,
    eventTags,
    isFollowing,
}: {
    community: GetCommunity;
    approxLocation: string;
    permissions: UserPermissions;
    refetch?: () => void;
    eventTags?: string[];
    isFollowing: boolean;
}) => {
    const searchParams = useSearchParams();
    const organizationName = searchParams?.get("organization");
    const isAdminOrManager =
        (permissions.isAdmin || permissions.isManager) ?? false;
    const startOfCurrentDay = useMemo(() => {
        return DateTime.now().startOf("day").toJSDate();
    }, []);
    const currentTime = useMemo(() => {
        return DateTime.fromJSDate(new Date())
            .setZone(DateTime.local().zoneName) // Set to user's timezone
            .toJSDate();
    }, []);
    const [currentlyVisibleInfo, setCurrentlyVisibleInfo] = useState<
        "events" | "people" | "more" | "merchandise" | "leaderboard"
    >("events");
    const [eventsNear, setEventsNear] = useState<GetAllEventsOutput[]>();
    const selectedTab = (searchParams?.get("tab") ?? "events-view") as
        | "events-view"
        | "calendar-view";

    // This is an attempt to use zustand instead of the context
    // so we can easily filter globally when necessary
    const {
        search,
        setSearch,
        debouncedSearch,
        eventFilters,
        setEventFilters,
    } = useFilterStore();

    const {
        pastEvents,
        onlyShowCurrentUserAttendingEvents,
        onlyShowCurrentUserHostingEvents,
        includeAttendeesNames,
        includeHostNames,
        geoLocation,
        useDateSortOverRide,
        dateRangeFilters,
    } = eventFilters;

    useSyncSearchWithURL();

    const [tags, setTags] = useState<string[]>([]);
    const [searchedTags, setSearchedTags] = useState<string[]>(eventTags ?? []);

    const handleSearchTags = useMemo(
        () =>
            debounce((value: string) => {
                if (value === "") {
                    setSearchedTags(eventTags ?? []);
                    return;
                }

                fuzzySearchTagsCommunityPage({
                    search: value,
                    limit: 3,
                    communitySlug: community.slug,
                })
                    .then((tags) => setSearchedTags(tags))
                    .catch(console.error);
            }, 300),
        [community.slug, eventTags],
    );

    const { data: queriedEvents, isFetching: isFetchingEvents } =
        api.event.getAll.useQuery(
            {
                searchTerm: debouncedSearch,
                includeAttendeesNames: true,
                includeHostNames,
                pastEvents,
                geoLocation,
                // we have to decide how to activate this filter
                sadgeFilterOn: true,
                communityFilter: {
                    id: community?.id,
                    slug: community?.slug,
                },
                onlyShowCurrentUserAttendingEvents,
                onlyShowCurrentUserHostingEvents,
                startOfCurrentDay: startOfCurrentDay,
                currentTime: currentTime,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                displayPinnedEvents: true,
                useDateSortOverRide,
                dateRangeFilters,
                tags: tags,
                showAdditionalHostUserInfo: true,
                take: community.hasMostlySatelliteEvents ? 300 : 70,
            },
            {
                enabled: !!community,
                refetchOnWindowFocus: false,
            },
        );

    const getFilteredEvents = useMemo(() => {
        if (!queriedEvents) return [];

        return queriedEvents.filter((event) => {
            // Organization filter
            if (organizationName) {
                const hasMatchingOrg = event.hosts.some(
                    (host) =>
                        host.user.organizationName?.toLowerCase() ===
                        organizationName.toLowerCase(),
                );
                if (!hasMatchingOrg) return false;
            }

            // Day filter
            const selectedDay = searchParams?.get("day");
            if (selectedDay) {
                const selectedDate = DateTime.fromFormat(
                    selectedDay,
                    "yyyy-MM-dd",
                ).startOf("day");
                const eventDate = DateTime.fromJSDate(
                    adjustTzToLocal(event.startTime).toJSDate(),
                ).startOf("day");
                if (!eventDate.hasSame(selectedDate, "day")) return false;
            }

            // Tags filter
            const tags =
                searchParams?.get("ftags")?.split(",").filter(Boolean) || [];
            if (tags.length > 0) {
                const hasMatchingTag = event.tags.some((t) => tags.includes(t));
                if (!hasMatchingTag) return false;
            }

            return true;
        });
    }, [
        queriedEvents,
        organizationName,
        searchParams?.get("day"),
        searchParams?.get("ftags"),
    ]);

    useEffect(() => {
        setEventsNear(getFilteredEvents);
    }, [getFilteredEvents]);

    const satelliteEffect = useMemo(() => {
        return community.hasMostlySatelliteEvents
            ? {
                  ...eventFilters,
                  useDateSortOverRide: true,
              }
            : eventFilters;
    }, [community.hasMostlySatelliteEvents]);

    useEffect(() => {
        setEventFilters(satelliteEffect);
    }, [satelliteEffect]);

    const handleLocationChange = (place: GooglePlace) => {
        const coords = extractCoordinates(place.geometry);
        if (!coords.latitude || !coords.longitude) return;
        setEventFilters({
            ...eventFilters,
            geoLocation: {
                latitude: coords.latitude,
                longitude: coords.longitude,
            },
        });
    };

    const { email: storeEmail } = useFollowingStore();

    const isFollowingApi = api.followers.isUserFollowing.useQuery(
        { communityId: community.id, email: storeEmail ?? undefined },
        {
            initialData: isFollowing,
        },
    );

    const shouldShowEvents = true; // !community.isFollowerGated || isFollowingApi.data;
    const isSatallite = !!community.hasMostlySatelliteEvents;

    const hideForRevenueCat = community.slug === "shipaton";

    return (
        <div className="flex w-full flex-1">
            <div className="flex w-full flex-col md:mb-10 md:gap-3 lg:flex-row lg:justify-between">
                {/* Desktop */}
                <span className="hidden w-inherit max-w-[870px] lg:block">
                    <div className="flex w-full flex-col">
                        <>
                            <div className="mb-4 flex w-full flex-col justify-between gap-4">
                                <If condition={!isSatallite}>
                                    <div className="flex max-w-[400px] items-center gap-4">
                                        <h5 className="w-fit whitespace-nowrap text-lg font-[500]">
                                            Events near
                                        </h5>
                                        <EventsNearInput
                                            placeholder={
                                                !approxLocation.includes("null")
                                                    ? approxLocation
                                                    : "Near you"
                                            }
                                            onLocationChange={
                                                handleLocationChange
                                            }
                                            className="rounded-full"
                                        />
                                    </div>
                                </If>
                                <If condition={isSatallite}>
                                    {!hideForRevenueCat && (
                                        <DayOfWeekFilter
                                            events={queriedEvents}
                                        />
                                    )}

                                    <CommunityTagsFilter
                                        community={community}
                                        events={queriedEvents}
                                        hideForRevenueCat={hideForRevenueCat}
                                    />
                                </If>
                                <div className="flex items-center justify-between">
                                    <GooeySearchInput
                                        placeholder="Search events and cities"
                                        className="w-full min-w-0 max-w-[90vw] rounded sm:w-[250px]"
                                        value={search}
                                        onChange={(e) =>
                                            setSearch(e.target.value)
                                        }
                                        isLoading={isFetchingEvents}
                                        iconComponent={<InnerSearchIcon />}
                                        suffixComponent={
                                            <EventTagsPopoverFilter
                                                placeholder="Event tags"
                                                onFilterChange={
                                                    handleSearchTags
                                                }
                                                onSelectonChange={setTags}
                                                options={searchedTags}
                                            />
                                        }
                                    />
                                    <div className="ml-4 flex h-full w-max flex-col items-center justify-end gap-2 self-center md:mb-0 md:flex-row xl:self-end ">
                                        <div className="flex w-max justify-end gap-1 rounded bg-v3-neutral p-1 ">
                                            <If
                                                condition={
                                                    !community.hasMostlySatelliteEvents
                                                }
                                            >
                                                <Button
                                                    variant="tabPrimary"
                                                    className={cn(
                                                        !pastEvents &&
                                                            !useDateSortOverRide &&
                                                            "bg-white text-black",
                                                    )}
                                                    onClick={() =>
                                                        setEventFilters({
                                                            ...eventFilters,
                                                            pastEvents: false,
                                                            useDateSortOverRide:
                                                                false,
                                                        })
                                                    }
                                                >
                                                    Closest to you
                                                </Button>
                                            </If>
                                            <Button
                                                variant="tabPrimary"
                                                className={cn(
                                                    !pastEvents &&
                                                        useDateSortOverRide &&
                                                        "bg-white text-black",
                                                )}
                                                onClick={() =>
                                                    setEventFilters({
                                                        ...eventFilters,
                                                        pastEvents: false,
                                                        useDateSortOverRide:
                                                            true,
                                                    })
                                                }
                                            >
                                                Upcoming
                                            </Button>
                                            <Button
                                                variant="tabPrimary"
                                                className={cn(
                                                    pastEvents &&
                                                        "bg-white text-black",
                                                )}
                                                onClick={() =>
                                                    setEventFilters({
                                                        ...eventFilters,
                                                        pastEvents: true,
                                                        useDateSortOverRide:
                                                            false,
                                                    })
                                                }
                                            >
                                                Past
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                        <div className="flex w-full max-w-[914px] flex-col gap-2 xl:max-w-none">
                            <If condition={!isSatallite}>
                                <If condition={shouldShowEvents}>
                                    <EventsGrid
                                        events={eventsNear?.slice(0, 4)}
                                        isLoading={!eventsNear}
                                        gridClassName="grid-nohang-last"
                                    />
                                </If>
                                <ConditionalCommunityBanner
                                    community={community!}
                                    permissions={permissions}
                                    isFollower={isFollowingApi.data}
                                    customBanner={community?.customBanner}
                                />

                                <If condition={shouldShowEvents}>
                                    <EventsGrid
                                        events={eventsNear?.slice(3)}
                                        isLoading={!eventsNear}
                                        gridClassName="grid-nohang-first"
                                    />
                                </If>
                            </If>
                            <If condition={isSatallite}>
                                <If condition={shouldShowEvents}>
                                    <ConditionalCommunityBanner
                                        community={community!}
                                        permissions={permissions}
                                        isFollower={isFollowingApi.data}
                                        customBanner={community?.customBanner}
                                    />
                                    <CondensedEventList
                                        events={eventsNear}
                                        permissions={permissions}
                                    />
                                </If>

                                <If condition={!shouldShowEvents}>
                                    <div className="relative flex flex-col gap-4">
                                        <div className="absolute top-5 z-30 w-full px-2 py-4">
                                            <ConditionalCommunityBanner
                                                community={community!}
                                                permissions={permissions}
                                                isFollower={isFollowingApi.data}
                                                customBanner={
                                                    community?.customBanner
                                                }
                                            />
                                        </div>
                                        <span className="pointer-events-none blur">
                                            <CondensedEventList
                                                events={eventsNear}
                                                permissions={permissions}
                                            />
                                        </span>
                                    </div>
                                </If>
                            </If>
                        </div>
                    </div>
                </span>

                {/* Mobile/smaller screens */}

                <span className="block w-inherit bg-v3-dark-gray lg:hidden">
                    <div
                        className={cn(
                            `small-scrollbar flex max-w-[calc(100vw-8px)] justify-center gap-2 overflow-x-auto
                                    border-b-[1px] border-v3-N-450 bg-v3-card-gray px-2 pb-0
                                    `,
                            (!!community.displayAffiliateProducts ||
                                !!community.leaderboard.length) &&
                                "justify-start",
                        )}
                    >
                        <Button
                            className={`rounded-none text-[16px] text-v3-light-gray hover:no-underline ${
                                currentlyVisibleInfo === "events"
                                    ? "border-b-2 border-v3-C400 text-white"
                                    : ""
                            }`}
                            variant="primaryLink"
                            onClick={() => setCurrentlyVisibleInfo("events")}
                        >
                            Events
                        </Button>
                        <Button
                            className={`rounded-none text-[16px] text-v3-light-gray hover:no-underline ${
                                currentlyVisibleInfo === "people"
                                    ? "border-b-2 border-v3-C400 text-white"
                                    : ""
                            }`}
                            variant="primaryLink"
                            onClick={() => setCurrentlyVisibleInfo("people")}
                        >
                            <span className="w-max">
                                People
                                {community.partnerTier.flatMap((p) => p.id)
                                    .length > 0 && ` & Partners`}
                            </span>
                        </Button>
                        <Button
                            className={`rounded-none text-[16px] text-v3-light-gray hover:no-underline ${
                                currentlyVisibleInfo === "more"
                                    ? "border-b-2 border-v3-C400 text-white"
                                    : ""
                            }`}
                            variant="primaryLink"
                            onClick={() => setCurrentlyVisibleInfo("more")}
                        >
                            More
                        </Button>
                        <If condition={!!community.displayAffiliateProducts}>
                            <Button
                                className={`rounded-none text-[16px] text-v3-light-gray hover:no-underline ${
                                    currentlyVisibleInfo === "merchandise"
                                        ? "border-b-2 border-v3-C400 text-white"
                                        : ""
                                }`}
                                variant="primaryLink"
                                onClick={() =>
                                    setCurrentlyVisibleInfo("merchandise")
                                }
                            >
                                Merch
                            </Button>
                        </If>
                        <If condition={!!community.leaderboard.length}>
                            <Link href={`/${community.slug}/leaderboard`}>
                                <Button
                                    className={`rounded-none text-[16px] text-v3-light-gray hover:no-underline ${
                                        currentlyVisibleInfo === "leaderboard"
                                            ? "border-b-2 border-v3-C400 text-white"
                                            : ""
                                    }`}
                                    variant="primaryLink"
                                >
                                    Leaderboard
                                </Button>
                            </Link>
                        </If>
                    </div>

                    <div className="mb-4" />
                    <If condition={currentlyVisibleInfo === "events"}>
                        <div className="flex w-full flex-col gap-4 md:px-4">
                            <If condition={!isSatallite}>
                                <div className="flex max-w-[400px] items-center gap-4 px-2">
                                    <h5 className="w-[200px] text-[20px]">
                                        Events near
                                    </h5>
                                    <EventsNearInput
                                        placeholder={approxLocation}
                                        onLocationChange={handleLocationChange}
                                        className="rounded-full"
                                    />
                                </div>
                            </If>
                            <If condition={isSatallite}>
                                <DayOfWeekFilter events={queriedEvents} />
                                <CommunityTagsFilter
                                    community={community}
                                    events={queriedEvents}
                                />
                            </If>
                            <div className="mb-2 flex flex-col justify-center gap-4 sm:mb-4 sm:flex-row sm:justify-between">
                                <div className="flex h-full w-full items-center gap-2 px-2 md:mb-0 md:flex-row xl:self-end ">
                                    <div className="flex w-max justify-end gap-1 rounded bg-v3-neutral p-1 ">
                                        <If
                                            condition={
                                                !community.hasMostlySatelliteEvents
                                            }
                                        >
                                            <Button
                                                variant="tabPrimary"
                                                className={cn(
                                                    !pastEvents &&
                                                        !useDateSortOverRide &&
                                                        "bg-white p-2 text-black",
                                                )}
                                                onClick={() =>
                                                    setEventFilters({
                                                        ...eventFilters,
                                                        pastEvents: false,
                                                        useDateSortOverRide:
                                                            false,
                                                    })
                                                }
                                            >
                                                Closest to you
                                            </Button>
                                        </If>
                                        <Button
                                            variant="tabPrimary"
                                            className={cn(
                                                !pastEvents &&
                                                    useDateSortOverRide &&
                                                    "bg-white p-2 text-black",
                                            )}
                                            onClick={() =>
                                                setEventFilters({
                                                    ...eventFilters,
                                                    pastEvents: false,
                                                    useDateSortOverRide: true,
                                                })
                                            }
                                        >
                                            Upcoming
                                        </Button>
                                        <Button
                                            variant="tabPrimary"
                                            className={cn(
                                                pastEvents &&
                                                    "bg-white p-2 text-black",
                                            )}
                                            onClick={() =>
                                                setEventFilters({
                                                    ...eventFilters,
                                                    pastEvents: true,
                                                    useDateSortOverRide: false,
                                                })
                                            }
                                        >
                                            Past
                                        </Button>
                                    </div>
                                    <TabSwitcherIcons
                                        communitySlug={community.slug}
                                        tab={selectedTab}
                                        className="ml-auto"
                                    />
                                </div>
                                <div className="flex h-full w-full flex-col items-center justify-end gap-2 self-center md:mb-0 md:flex-row xl:self-end ">
                                    <GooeySearchInput
                                        placeholder="Search events and cities"
                                        className="w-full min-w-0 max-w-[95vw] rounded"
                                        value={search}
                                        onChange={(e) =>
                                            setSearch(e.target.value)
                                        }
                                        isLoading={isFetchingEvents}
                                        iconComponent={<InnerSearchIcon />}
                                        suffixComponent={
                                            <EventTagsPopoverFilter
                                                placeholder="Event tags"
                                                onFilterChange={
                                                    handleSearchTags
                                                }
                                                onSelectonChange={setTags}
                                                options={searchedTags}
                                            />
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        <If condition={!isSatallite}>
                            <div className="flex flex-col gap-4">
                                <If condition={shouldShowEvents}>
                                    <EventsGrid
                                        events={eventsNear?.slice(0, 2)}
                                        isLoading={!eventsNear}
                                    />
                                </If>
                                <ConditionalCommunityBanner
                                    community={community!}
                                    permissions={permissions}
                                    isFollower={isFollowingApi.data}
                                    customBanner={community?.customBanner}
                                />
                                <If condition={shouldShowEvents}>
                                    <EventsGrid
                                        events={eventsNear?.slice(2)}
                                        isLoading={!eventsNear}
                                    />
                                </If>
                            </div>
                        </If>
                        <If condition={isSatallite}>
                            <If condition={shouldShowEvents}>
                                <CondensedEventList
                                    events={eventsNear?.slice(0, 3)}
                                    permissions={permissions}
                                />
                                <div className="px-2 py-4">
                                    <ConditionalCommunityBanner
                                        community={community!}
                                        permissions={permissions}
                                        isFollower={isFollowingApi.data}
                                        customBanner={community?.customBanner}
                                    />
                                </div>
                                <CondensedEventList
                                    events={eventsNear?.slice(3)}
                                    permissions={permissions}
                                />
                                <span className="block h-4" />
                            </If>
                            <If condition={!shouldShowEvents}>
                                <div className="relative flex flex-col gap-4">
                                    <div className="absolute top-5 z-30 w-full px-2 py-4">
                                        <ConditionalCommunityBanner
                                            community={community!}
                                            permissions={permissions}
                                            isFollower={isFollowingApi.data}
                                            customBanner={
                                                community?.customBanner
                                            }
                                        />
                                    </div>
                                    <span className="pointer-events-none blur">
                                        <CondensedEventList
                                            events={eventsNear}
                                            permissions={permissions}
                                        />
                                    </span>
                                </div>
                            </If>
                        </If>
                    </If>

                    <If condition={currentlyVisibleInfo === "people"}>
                        <CommunityPagePeopleTab
                            community={community}
                            hasPermissions={isAdminOrManager}
                            refetch={refetch}
                        />
                    </If>

                    <If condition={currentlyVisibleInfo === "more"}>
                        <MoreInfoTab community={community} />
                    </If>

                    <If condition={currentlyVisibleInfo === "merchandise"}>
                        <MerchTab community={community} />
                    </If>
                </span>

                {/* Only show in this role IF it's desktop*/}
                <span className="hidden lg:block">
                    <CommunityPagePeopleTab
                        community={community}
                        hasPermissions={isAdminOrManager}
                        refetch={refetch}
                    />

                    <div className="flex flex-col gap-2">
                        {community.partnerTier
                            .sort((a, b) => a.order - b.order)
                            .map((partnerTier) => {
                                if (!partnerTier.partners.length) return null;
                                return (
                                    <Card key={partnerTier.id}>
                                        <CardHeader>
                                            <Label>{partnerTier.name}</Label>
                                        </CardHeader>
                                        <CardContent
                                            className={cn(
                                                partnerTier.renderType ===
                                                    "GRID"
                                                    ? "grid grid-cols-2 gap-2"
                                                    : "flex flex-col gap-2",
                                            )}
                                        >
                                            {partnerTier.partners.map((p) => {
                                                return (
                                                    <Card
                                                        className="flex h-[100px] max-w-[261px] gap-2 bg-v3-N-500 p-3"
                                                        key={p.id}
                                                    >
                                                        <Link
                                                            href={p.url}
                                                            target="_blank"
                                                            className="relative h-full w-full"
                                                        >
                                                            <Image
                                                                src={`${process.env.NEXT_PUBLIC_ASSETS_URL}/community/${community.slug}/partners/${p.image}`}
                                                                fill
                                                                style={{
                                                                    objectFit:
                                                                        "contain",
                                                                }}
                                                                alt="Partner logo"
                                                            />
                                                        </Link>
                                                    </Card>
                                                );
                                            })}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                    </div>
                </span>
            </div>
        </div>
    );
};

export default CommunityEventsFeed;
